import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as TFF from '@tff/types/tff';
import TFFAccordionNew from '../../../../styled-components/TFFAccordionNew';
import TFFTypography from '../../../../styled-components/TFFTypography';
import FlightStopsPath from '../FlightStopsPath';
import NdcEditSegments from './NdcEditSegments';

interface NdcChangeFlightCardProps {
  orderDetails: TFF.OrderDetails;
  journey: TFF.Journey;
  airports: TFF.Airport[];
  setLowerClass: (value: boolean) => void;
  setSelectedCabinClass: (value: string) => void;
  selectedCabinClass: string;
}

const isExpired = (dateString: string): boolean => new Date() > new Date(dateString);

const NdcChangeFlightCard: React.FC<NdcChangeFlightCardProps> = ({
  orderDetails,
  journey,
  airports,
  setLowerClass,
  setSelectedCabinClass,
  selectedCabinClass,
}) => {
  const intl = useIntl();

  const capitalizedCabinClass = useMemo(() => {
    const className = journey.CabinClassName ?? journey.Segments?.[0]?.CabinClassName;
    return className ? className.charAt(0).toUpperCase() + className.slice(1).toLowerCase() : '';
  }, [journey]);

  const isOutbound = journey.Ond === orderDetails.Journeys[0].Ond;
  const labelIcons = isOutbound
    ? { origin: 'aircraft-up', destination: 'aircraft-down' }
    : { origin: 'aircraft-down', destination: 'aircraft-up' };

  const firstSegment = journey.Segments[0];

  return (
    <div>
      {isExpired(journey.Segments[0].DepartureTime) ? (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '40px' }}>
          <TFFTypography
            text={`${firstSegment.MarketingCarrier} ${firstSegment.FlightNumber}`}
            fontWeight="bold"
            color="color-theme-variant"
          />
          <FlightStopsPath
            journey={journey}
            capitalizedCabinClass={capitalizedCabinClass}
            showAirportsName={airports}
            labelIcons={labelIcons}
          />
        </div>
      ) : (
        <TFFAccordionNew
          accordionId={`ndc-edit-${journey.JourneyIdentifier}`}
          summaryChildren={
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '40px' }}>
              <TFFTypography
                text={`${firstSegment.MarketingCarrier} ${firstSegment.FlightNumber}`}
                fontWeight="bold"
                color="color-theme-variant"
              />
              <FlightStopsPath
                journey={journey}
                capitalizedCabinClass={capitalizedCabinClass}
                showAirportsName={airports}
                labelIcons={labelIcons}
              />
            </div>
          }
          controlText={intl.formatMessage({ id: 'rebook.editFlight' })}
          headerAlignBottom
          detailsChildren={
            <NdcEditSegments
              orderDetails={orderDetails}
              journey={journey}
              setLowerClass={setLowerClass}
              setSelectedCabinClass={setSelectedCabinClass}
              selectedCabinClass={selectedCabinClass}
            />
          }
          detailsColor="var(--color-functional-neutral-100)"
        />
      )}
    </div>
  );
};

export default NdcChangeFlightCard;
